import clsx from "clsx";
import { createContext, HTMLProps, useContext } from "react";

type SwitchRadioGroupContextType = {
  name?: string;
  disabled?: boolean;
  value?: string | number | boolean | readonly string[];
};

const SwitchRadioGroupContext = createContext<SwitchRadioGroupContextType>({});
export const useSwitchRadioGroupValue = () => useContext(SwitchRadioGroupContext);

export const TextSwitch = ({
  hasBorder = true,
  children,
  className,
  disabled = false,
  name = "default",
  value,
}: HTMLProps<HTMLDivElement> & {
  hasBorder?: boolean;
  disabled?: boolean;
}) => {
  return (
    <SwitchRadioGroupContext.Provider value={{ name, disabled, value }}>
      <div
        role="radiogroup"
        aria-label={name}
        className={clsx(
          "flex text-xs items-center",
          hasBorder && "border border-border-action rounded-lg p-0.5 bg-white",
          className
        )}
      >
        {children}
      </div>
    </SwitchRadioGroupContext.Provider>
  );
};

export const SwitchLabel = <T extends string | number | boolean | readonly string[]>({
  children,
  value,
  className,
  onToggle,
}: HTMLProps<HTMLLabelElement> & { onToggle: (value: T) => void; value: T }) => {
  const { name, disabled, value: activeValue } = useSwitchRadioGroupValue();
  const isChecked = activeValue === value;

  return (
    <label
      id={`${name}-${value}-label`}
      htmlFor={`${name}-${value}`}
      data-checked={isChecked}
      data-testid={`${name}-${value}-label`}
      className={clsx(
        "flex! flex-1 items-center justify-center h-full p-1.5 gap-2.5 rounded-lg",
        "cursor-pointer select-none transition-all",
        isChecked ? "text-text-base-active bg-background-button-secondary" : "text-text-base-secondary",
        disabled && "opacity-50",
        className
      )}
    >
      {children}
      <input
        data-testid="text-switch-input"
        id={`${name}-${value}`}
        name={`${name}-${value}`}
        aria-labelledby={`${name}-${value}-label`}
        type="radio"
        disabled={disabled}
        className="hidden"
        checked={isChecked}
        value={value}
        onChange={() => onToggle(value)}
      />
    </label>
  );
};

TextSwitch.Label = SwitchLabel;

export const SwitchTag = ({ className, children }: HTMLProps<HTMLDivElement>) => {
  return (
    <div
      className={clsx(
        "bg-background-badge-purple text-text-accent-brand-purple-primary text-xs rounded-sm px-0.5",
        className
      )}
    >
      {children}
    </div>
  );
};
TextSwitch.Tag = SwitchTag;
